import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart
} from 'recharts';
import { Calendar, Filter, BarChart2, TrendingUp } from 'lucide-react';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1', '#a4de6c', '#d0ed57'];
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export default function ControlOperativoDashboard({ orders = [], brigades = [] }) {
  // Filters
  const [periodType, setPeriodType] = useState('month'); // day, month, year
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth()); // 0-11
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedBrigadeId, setSelectedBrigadeId] = useState('all');

  const years = [2023, 2024, 2025];
  // Filter Logic
  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      // Brigade Filter
      if (selectedBrigadeId !== 'all' && String(o.brigade_user_id || o.brigadaUserId || '') !== String(selectedBrigadeId)) {
        return false;
      }

      const d = new Date(o.date); // Assuming o.date is YYYY-MM-DD
      const year = d.getFullYear();
      const month = d.getMonth();
      const dateStr = o.date; // YYYY-MM-DD

      // Period Filter
      if (periodType === 'year') {
        return year === parseInt(selectedYear);
      } else if (periodType === 'month') {
        return year === parseInt(selectedYear) && month === parseInt(selectedMonth);
      } else if (periodType === 'day') {
        return dateStr === selectedDate;
      }
      return true;
    });
  }, [orders, periodType, selectedYear, selectedMonth, selectedDate, selectedBrigadeId]);

  // Metrics
  const metrics = useMemo(() => {
    const total = filteredOrders.length;
    const completed = filteredOrders.filter(o => 
      String(o.status).toLowerCase().includes('complet') || 
      String(o.status).toLowerCase().includes('ejecut')
    ).length;
    const efficiency = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;
    
    return { total, completed, efficiency };
  }, [filteredOrders]);

  // Chart Data Preparation

  // 1. Production by Period (Bar Chart)
  // If Year selected -> Group by Month
  // If Month selected -> Group by Day
  // If Day selected -> Group by Hour (if time avail) or just show single bar? Maybe show by Brigade for Day view.
  const productionByPeriodData = useMemo(() => {
    const data = [];
    if (periodType === 'year') {
      // Group by months
      for (let i = 0; i < 12; i++) {
        data.push({ name: MONTHS[i].substring(0, 3), count: 0, fullDate: `${selectedYear}-${i}` });
      }
      filteredOrders.forEach(o => {
        const m = new Date(o.date).getMonth();
        if (data[m]) data[m].count++;
      });
    } else if (periodType === 'month') {
      // Group by days
      const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
      for (let i = 1; i <= daysInMonth; i++) {
        data.push({ name: `${i}`, count: 0 });
      }
      filteredOrders.forEach(o => {
        const d = new Date(o.date).getDate();
        if (data[d-1]) data[d-1].count++;
      });
    } else {
      // Day view - maybe by hour if time exists, or just summary
      // Let's do by Brigade for Day view to make it useful, or just 1 bar
      // Actually, let's just show hourly distribution if possible, else summary
      const hours = {};
      for (let i=6; i<=20; i++) hours[i] = 0; // 6am to 8pm
      filteredOrders.forEach(o => {
        if (o.time) {
          const h = parseInt(o.time.split(':')[0]);
          if (hours[h] !== undefined) hours[h]++;
        }
      });
      Object.keys(hours).forEach(h => {
        data.push({ name: `${h}:00`, count: hours[h] });
      });
    }
    return data;
  }, [filteredOrders, periodType, selectedYear, selectedMonth]);

  // 2. Production by Brigade (Comparative)
  const productionByBrigadeData = useMemo(() => {
    const map = {};
    brigades.forEach(b => map[b.id] = { name: b.nombre, count: 0, completed: 0 });
    // Add 'Unknown' if needed, but let's stick to known brigades
    
    filteredOrders.forEach(o => {
      const bid = o.brigade_user_id || o.brigadaUserId;
      if (bid && map[bid]) {
        map[bid].count++;
        if (String(o.status).toLowerCase().includes('complet')) {
          map[bid].completed++;
        }
      }
    });
    return Object.values(map).filter(b => b.count > 0);
  }, [filteredOrders, brigades]);

  // 3. Trend (Line) - Similar to period but maybe just line
  // Let's use the same data as Period but visualize as Line for "Trend"
  // Or if Year is selected, maybe show last 3 years? 
  // The requirement says "Tendencia de Producción - Líneas que muestran la evolución temporal"
  // If filter is Month, show daily trend.
  const trendData = productionByPeriodData;

  // 4. Distribution by Product (Pie)
  // Using activityClassification or work
  const distributionData = useMemo(() => {
    const map = {};
    filteredOrders.forEach(o => {
      const key = o.activityClassification || 'Sin Clasificación';
      map[key] = (map[key] || 0) + 1;
    });
    return Object.keys(map).map(k => ({ name: k, value: map[k] }));
  }, [filteredOrders]);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Filters Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Filter className="text-gray-500" size={20} />
            <h3 className="font-semibold text-gray-700">Filtros de Dashboard</h3>
          </div>
          
          <div className="flex flex-wrap gap-3 items-center">
            {/* Period Type */}
            <div className="flex rounded-md shadow-sm">
              <button
                onClick={() => setPeriodType('day')}
                className={`px-3 py-1.5 text-sm border rounded-l-md ${periodType === 'day' ? 'bg-blue-50 border-blue-200 text-blue-700 font-medium' : 'bg-white border-gray-300 text-gray-700'}`}
              >
                Día
              </button>
              <button
                onClick={() => setPeriodType('month')}
                className={`px-3 py-1.5 text-sm border-t border-b ${periodType === 'month' ? 'bg-blue-50 border-blue-200 text-blue-700 font-medium' : 'bg-white border-gray-300 text-gray-700'}`}
              >
                Mes
              </button>
              <button
                onClick={() => setPeriodType('year')}
                className={`px-3 py-1.5 text-sm border rounded-r-md ${periodType === 'year' ? 'bg-blue-50 border-blue-200 text-blue-700 font-medium' : 'bg-white border-gray-300 text-gray-700'}`}
              >
                Año
              </button>
            </div>

            {/* Date Selectors based on Type */}
            {periodType === 'day' && (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              />
            )}

            {(periodType === 'month' || periodType === 'year') && (
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}

            {periodType === 'month' && (
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              >
                {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
            )}

            {/* Brigade Filter */}
            <select
              value={selectedBrigadeId}
              onChange={(e) => setSelectedBrigadeId(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none min-w-[150px]"
            >
              <option value="all">Todas las Brigadas</option>
              {brigades.map(b => (
                <option key={b.id} value={b.id}>{b.brigada || b.nombre}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">Total de Órdenes</p>
            <h4 className="text-2xl font-bold text-gray-900 mt-1">{metrics.total}</h4>
          </div>
          <div className="p-3 bg-blue-50 rounded-full text-blue-600">
            <Calendar size={24} />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">Órdenes Completadas</p>
            <h4 className="text-2xl font-bold text-green-600 mt-1">{metrics.completed}</h4>
          </div>
          <div className="p-3 bg-green-50 rounded-full text-green-600">
            <BarChart2 size={24} />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">Eficiencia / Calidad</p>
            <h4 className="text-2xl font-bold text-indigo-600 mt-1">{metrics.efficiency}%</h4>
          </div>
          <div className="p-3 bg-indigo-50 rounded-full text-indigo-600">
            <TrendingUp size={24} />
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Production by Period */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">Producción por Período</h4>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={productionByPeriodData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  cursor={{ fill: '#F3F4F6' }}
                />
                <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Órdenes" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Production by Brigade */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">Producción por Brigada</h4>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={productionByBrigadeData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E5E7EB" />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
                <YAxis dataKey="name" type="category" width={100} axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  cursor={{ fill: '#F3F4F6' }}
                />
                <Legend />
                <Bar dataKey="count" fill="#8B5CF6" radius={[0, 4, 4, 0]} name="Total" barSize={20} />
                <Bar dataKey="completed" fill="#10B981" radius={[0, 4, 4, 0]} name="Completadas" barSize={20} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">Tendencia de Producción</h4>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                />
                <Line type="monotone" dataKey="count" stroke="#F59E0B" strokeWidth={3} dot={{ r: 4, fill: '#F59E0B' }} activeDot={{ r: 6 }} name="Órdenes" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Distribution by Product */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">Distribución por Producto (Clasificación)</h4>
          <div className="h-[300px] w-full flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={distributionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {distributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                />
                <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
